import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/lib/LanguageContext";
import useSEO, { SITE_URL, SITE_NAME } from "@/lib/useSEO";
import { getFeatured, getRest, getPublished, CATEGORIES, localize } from "@/content/blog";

// Hero image for the blog landing — luxury Baja coastline mood.
const HERO_IMAGE =
    "https://images.unsplash.com/photo-1509233725247-49e657c54213?auto=format&fit=crop&w=2400&q=85";

function fmtDate(iso, lang) {
    try {
        // Treat publishedAt as a calendar date (YYYY-MM-DD), not a UTC timestamp,
        // so it never shifts a day back in negative-UTC timezones.
        const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
        const dt = new Date(y, (m || 1) - 1, d || 1);
        return dt.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
            year: "numeric", month: "long", day: "numeric",
        });
    } catch (_) { return iso; }
}

function categoryLabel(key, lang) {
    return (CATEGORIES[key] && CATEGORIES[key][lang]) || CATEGORIES[key]?.en || key;
}

export default function Blog() {
    const { lang, t } = useLanguage();
    const featured = getFeatured();
    const rest = getRest();
    const all = getPublished();

    // Blog schema — landing page carries an aggregated Blog with a summary
    // of every published post. Each individual article carries its own
    // BlogPosting schema on its detail page.
    const blogSchema = {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: `${SITE_NAME} — Journal`,
        url: `${SITE_URL}/blog`,
        publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
            logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/brand/pkm-icon.png`,
            },
        },
        blogPost: all.map((a) => {
            const loc = localize(a, "en");
            return {
                "@type": "BlogPosting",
                headline: loc.title,
                description: loc.excerpt,
                datePublished: a.publishedAt,
                dateModified: a.updatedAt,
                url: `${SITE_URL}/blog/${a.slug}`,
                image: a.featuredImage.ogUrl || a.featuredImage.url,
                author: {
                    "@type": "Organization",
                    name: a.author.name,
                    url: a.author.url,
                },
            };
        }),
    };

    useSEO({
        title:
            lang === "es"
                ? "Journal — Guías de Propiedad de Lujo en Los Cabos"
                : "Journal — Luxury Home & Ownership Guides for Los Cabos",
        description:
            lang === "es"
                ? "Ideas, guías y notas editoriales de Pacific Key Management para propietarios de residencias de lujo en Los Cabos — preparación de temporada, mantenimiento, estilo de vida y más."
                : "Editorial notes, guides and ownership essays from Pacific Key Management for luxury Los Cabos homeowners — seasonal preparation, maintenance, lifestyle and more.",
        path: "/blog",
        image: featured ? featured.featuredImage.ogUrl || featured.featuredImage.url : undefined,
        breadcrumbs: [
            { name: lang === "es" ? "Inicio" : "Home", path: "/" },
            { name: "Journal", path: "/blog" },
        ],
        schema: blogSchema,
    });

    return (
        <div data-testid="blog-page" className="bg-pearl">
            <PageHero
                eyebrow="Journal"
                title={lang === "es" ? "Guías editoriales para propietarios en Los Cabos." : "Editorial guides for Los Cabos homeowners."}
                subtitle={
                    lang === "es"
                        ? "Notas curadas de nuestro equipo — preparación estacional, mantenimiento preventivo y las decisiones tranquilas detrás de una residencia bien administrada."
                        : "Curated notes from our team — seasonal preparation, preventative maintenance, and the quiet decisions behind a well-run residence."
                }
                image={HERO_IMAGE}
                height="h-[60vh]"
            />

            {/* Featured article */}
            {featured && (
                <section className="py-24" data-testid="blog-featured">
                    <div className="container-luxe">
                        <Reveal>
                            <p className="overline text-gold/80 mb-6">
                                {lang === "es" ? "Artículo Destacado" : "Featured Article"}
                            </p>
                        </Reveal>
                        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                            <Reveal className="lg:col-span-7 order-2 lg:order-1">
                                <Link
                                    to={`/blog/${featured.slug}`}
                                    className="block group overflow-hidden bg-navy/5"
                                    data-testid={`blog-featured-image-${featured.slug}`}
                                >
                                    <img
                                        src={featured.featuredImage.url}
                                        alt={featured.featuredImage.alt}
                                        loading="eager"
                                        className="w-full h-[340px] sm:h-[440px] lg:h-[520px] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                    />
                                </Link>
                            </Reveal>
                            <Reveal delay={100} className="lg:col-span-5 order-1 lg:order-2">
                                <span className="inline-block text-[0.62rem] tracking-[0.32em] uppercase text-gold border border-gold/40 px-3 py-1.5 mb-6">
                                    {categoryLabel(featured.category, lang)}
                                </span>
                                <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-[2.6rem] leading-[1.15] mb-6 tracking-tight">
                                    <Link to={`/blog/${featured.slug}`} className="hover:text-gold transition-colors" data-testid={`blog-featured-title-${featured.slug}`}>
                                        {localize(featured, lang).title}
                                    </Link>
                                </h2>
                                <p className="text-navy/70 text-base sm:text-lg leading-relaxed mb-8">
                                    {localize(featured, lang).excerpt}
                                </p>
                                <div className="flex items-center gap-5 text-xs tracking-widest uppercase text-navy/50 mb-8">
                                    <span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gold" strokeWidth={1.25} />{fmtDate(featured.publishedAt, lang)}</span>
                                    {featured.readingMinutes && (
                                        <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-gold" strokeWidth={1.25} />{featured.readingMinutes} {lang === "es" ? "min" : "min read"}</span>
                                    )}
                                </div>
                                <Link
                                    to={`/blog/${featured.slug}`}
                                    data-testid={`blog-featured-cta-${featured.slug}`}
                                    className="inline-flex items-center gap-3 text-navy border-b border-navy/40 pb-1.5 text-[0.72rem] tracking-[0.28em] uppercase hover:text-gold hover:border-gold transition-colors"
                                >
                                    {lang === "es" ? "Leer Artículo" : "Read Article"}
                                    <ArrowRight className="w-4 h-4" strokeWidth={1.25} />
                                </Link>
                            </Reveal>
                        </div>
                    </div>
                </section>
            )}

            {/* Article grid or empty state */}
            <section className="pb-28" data-testid="blog-list">
                <div className="container-luxe">
                    {rest.length > 0 && (
                        <>
                            <Reveal>
                                <div className="flex items-end justify-between mb-14 border-t border-navy/10 pt-14">
                                    <div>
                                        <p className="overline text-gold/80 mb-4">{lang === "es" ? "Más Artículos" : "More Articles"}</p>
                                        <h3 className="font-serif text-navy text-2xl sm:text-3xl tracking-tight">
                                            {lang === "es" ? "Del equipo editorial." : "From the editorial team."}
                                        </h3>
                                    </div>
                                </div>
                            </Reveal>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
                                {rest.map((a) => {
                                    const loc = localize(a, lang);
                                    return (
                                        <Reveal key={a.slug}>
                                            <article className="group" data-testid={`blog-card-${a.slug}`}>
                                                <Link to={`/blog/${a.slug}`} className="block overflow-hidden bg-navy/5 mb-6">
                                                    <img
                                                        src={a.featuredImage.url}
                                                        alt={a.featuredImage.alt}
                                                        loading="lazy"
                                                        className="w-full h-64 object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                                                    />
                                                </Link>
                                                <span className="text-[0.62rem] tracking-[0.32em] uppercase text-gold">{categoryLabel(a.category, lang)}</span>
                                                <h4 className="font-serif text-navy text-xl sm:text-2xl leading-tight mt-3 mb-4 tracking-tight">
                                                    <Link to={`/blog/${a.slug}`} className="hover:text-gold transition-colors" data-testid={`blog-card-title-${a.slug}`}>
                                                        {loc.title}
                                                    </Link>
                                                </h4>
                                                <p className="text-navy/70 text-sm leading-relaxed mb-4">{loc.excerpt}</p>
                                                <p className="text-[0.65rem] tracking-widest uppercase text-navy/50">{fmtDate(a.publishedAt, lang)}</p>
                                            </article>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {all.length === 0 && (
                        <Reveal>
                            <div className="text-center py-24">
                                <p className="text-navy/60 italic">
                                    {lang === "es" ? "Próximamente artículos editoriales." : "Editorial articles coming soon."}
                                </p>
                            </div>
                        </Reveal>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-navy text-pearl py-20" data-testid="blog-cta">
                <div className="container-luxe grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-8">
                        <p className="overline text-gold/80 mb-4">{lang === "es" ? "Manténgase Informado" : "Stay Considered"}</p>
                        <h3 className="font-serif text-3xl sm:text-4xl leading-tight tracking-tight mb-3">
                            {lang === "es" ? "¿Preguntas sobre su residencia?" : "Questions about your residence?"}
                        </h3>
                        <p className="text-pearl/75 max-w-2xl">
                            {lang === "es"
                                ? "Nuestro equipo está disponible para una conversación privada sobre operaciones, mantenimiento estacional o cualquier tema editorial que le interese."
                                : "Our team is available for a private conversation about operations, seasonal maintenance, or any editorial topic that interests you."}
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/contact"
                            data-testid="blog-cta-contact"
                            className="inline-flex items-center gap-3 border border-gold/60 text-gold px-6 py-3.5 text-[0.7rem] tracking-[0.28em] uppercase hover:bg-gold hover:text-navy transition-colors duration-300"
                        >
                            {t("common.requestConsultation")}
                            <ArrowRight className="w-4 h-4" strokeWidth={1.25} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
